import React, { useState, useEffect } from 'react';
import FamilyCalendar from './FamilyCalendar';
import { parseGedcomToTree } from '../utils/gedcomParser';
import './EnhancedComponents.css';

const Calendar = () => {
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [gedcomData, setGedcomData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadGedcomData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        const response = await fetch('/data/rossetti.ged');
        
        if (response.ok) {
          const content = await response.text();
          
          const parsed = parseGedcomToTree(content);
          
          setGedcomData(parsed);
        } else {
          throw new Error(`Failed to fetch GEDCOM file: ${response.status} ${response.statusText}`);
        }
      } catch (error) {
        console.error('Calendar: Error loading GEDCOM data:', error);
        setError(error.message);
      } finally {
        setIsLoading(false);
      }
    };

    loadGedcomData();
  }, []);

  const handleOpenCalendar = () => {
    setIsCalendarOpen(true);
  };

  const closeCalendar = () => {
    setIsCalendarOpen(false);
  };

  return (
    <section id="calendar" className="calendar-section">
      <div className="container">
        <div className="section-icon">
          <img src="/images/calendar.png" alt="Calendar" />
        </div>
        
        <h2>Family Calendar</h2>
        
        <div className="calendar-intro">
          <p>
            Explore our family's most important dates and milestones through an interactive calendar experience. 
            From historical births and marriages to current year anniversaries and upcoming celebrations, 
            discover the rhythm of our family's journey through time.
          </p>
        </div>

        <div className="calendar-features">
          <div className="feature-grid">
            <div className="feature-card">
              <div className="feature-icon">🎂</div>
              <h3>Birthdays & Anniversaries</h3>
              <p>Never miss a family birthday or wedding anniversary. See upcoming celebrations and milestone years.</p>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon">📅</div>
              <h3>Historical Timeline</h3>
              <p>Browse through decades of family history with births, marriages, and significant life events.</p>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon">🌹</div>
              <h3>Memorial Dates</h3>
              <p>Honor and remember loved ones with memorial anniversaries and remembrance dates.</p>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon">🎉</div>
              <h3>Upcoming Events</h3>
              <p>Stay informed about family celebrations and important dates coming up in the next 30 days.</p>
            </div>
          </div>
        </div>

        <div className="calendar-stats">
          {isLoading && (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Loading family data...</p>
            </div>
          )}
          
          {error && (
            <div className="error-state">
              <div className="error-icon">⚠️</div>
              <p>Error loading family data: {error}</p>
            </div>
          )}
          
          {gedcomData && !isLoading && !error && (
            <div className="stats-preview">
              <div className="stat-item">
                <div className="stat-number">
                  {Object.keys(gedcomData.individuals || {}).length}
                </div>
                <div className="stat-label">Family Members</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">
                  {Object.keys(gedcomData.families || {}).length}
                </div>
                <div className="stat-label">Family Units</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">
                  {new Date().getFullYear() - 1866}+
                </div>
                <div className="stat-label">Years of History</div>
              </div>
            </div>
          )}
        </div>

        <div className="calendar-description">
          <h3>Interactive Calendar Views</h3>
          <div className="view-descriptions">

            <div className="view-item">
              <strong>Upcoming View:</strong> List layout showing upcoming anniversaries
            </div>

            <div className="view-item">
              <strong>Month View:</strong> Traditional calendar layout showing daily events
            </div>

            <div className="view-item">
              <strong>Year View:</strong> Annual overview with event counts and quick navigation between months
            </div>

            <div className="view-item">
              <strong>Timeline View:</strong> Chronological listing of all family events with detailed information
            </div>

          </div>
        </div>

        <div className="cta-buttons">
          <button 
            className="cta-btn primary" 
            onClick={handleOpenCalendar}
            disabled={isLoading || error || !gedcomData}
          >
            <i className="fas fa-calendar-alt"></i>
            {isLoading ? 'Loading Family Data...' : 
             error ? 'Data Unavailable' : 
             'Open Family Calendar'}
          </button>
          
          {error && (
            <div className="error-message">
              <p>Unable to load family data. Please check the console for details.</p>
            </div>
          )}
        </div>
      </div>

      {isCalendarOpen && (
        <FamilyCalendar 
          gedcomData={gedcomData}
          onClose={closeCalendar}
        />
      )}
    </section>
  );
};

export default Calendar;
